import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, StopCircle, AlertCircle, X, Sparkles, RotateCcw } from 'lucide-react';
import { RoaryMessage } from './RoaryMessage';
import type { ChatMessage, QuickReply, ConversationPhase } from '../../hooks/useRoary';
import roaryMascot from '/assets/Roary-mascot.png';

/** The restored-chat notice stays up until the user sends the next message. */
export function isRestoredConversationWaiting(
  messages: Array<{ id: string; role: string }>
): boolean {
  const index = messages.findIndex(message => message.id.startsWith('welcome-back-'));
  if (index < 0) return false;
  return !messages.slice(index + 1).some(message => message.role === 'user');
}

interface RoaryChatProps {
  messages: ChatMessage[];
  isLoading: boolean;
  isStreaming: boolean;
  isThinking: boolean;
  currentToolName: string | null;
  error: string | null;
  phase?: ConversationPhase;
  onSendMessage: (content: string) => void;
  onQuickReply?: (reply: QuickReply) => void;
  onCancel: () => void;
  onClearError: () => void;
  darkMode?: boolean;
  isDepleted?: boolean;
  /** A previous conversation was restored. Offers a visible way to discard it. */
  cachedAttempt?: boolean;
  onStartOver?: () => void;
}

// Format tool name for display - human-friendly mappings
const formatToolName = (name: string): string => {
  const mappings: Record<string, string> = {
    list_system_templates: 'Browsing templates',
    create_site_from_template: 'Creating site from template',
    create_site: 'Creating your website',
    update_site_settings: 'Configuring site settings',
    get_available_themes: 'Loading available themes',
    get_site_summary: 'Reviewing site structure',
    create_page: 'Building a new page',
    update_page: 'Updating page settings',
    delete_page: 'Removing page',
    list_pages: 'Loading pages',
    add_component: 'Adding section to page',
    add_layout_component: 'Adding section to layout',
    update_component: 'Updating section content',
    delete_component: 'Removing section',
    reorder_components: 'Organizing page sections',
    get_component_schema: 'Preparing component',
    create_layout: 'Setting up page layout',
    assign_layout_to_page: 'Applying layout to page',
    update_layout: 'Updating layout',
    create_blog: 'Setting up your blog',
    create_blog_post: 'Writing blog post',
    update_blog_post: 'Updating blog post',
    update_navbar: 'Configuring navigation menu',
    update_footer: 'Setting up footer',
    search_stock_images: 'Finding perfect images',
    upload_image: 'Uploading image',
  };
  return mappings[name] || name.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
};

// Dynamic placeholder text based on conversation phase
const getPlaceholder = (
  phase?: ConversationPhase,
  isStreaming?: boolean,
  isDepleted?: boolean
): string => {
  if (isDepleted) return 'Out of marshmallows — resets next billing cycle';
  if (isStreaming) return 'Roary is working...';
  if (phase === 'greeting') return 'Say hi to Roary or click a button above...';
  if (phase === 'building') return 'Roary is building your site...';
  return 'Chat with Roary...';
};

export const RoaryChat: React.FC<RoaryChatProps> = ({
  messages,
  isLoading,
  isStreaming,
  isThinking,
  currentToolName,
  error,
  phase,
  onSendMessage,
  onQuickReply,
  onCancel,
  onClearError,
  darkMode = false,
  isDepleted = false,
  cachedAttempt = false,
  onStartOver,
}) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking, currentToolName]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  // The box is disabled while Roary is writing, which drops the cursor.
  // Put it back as soon as the user can type a reply.
  const composerReady = phase !== 'greeting' && !isStreaming && !isLoading && !isDepleted;
  useEffect(() => {
    if (!composerReady) return;
    inputRef.current?.focus();
  }, [composerReady]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isStreaming || isLoading || isDepleted) return;

    onSendMessage(input.trim());
    setInput('');

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className={`flex flex-col h-full ${darkMode ? 'bg-transparent' : 'bg-gray-50'}`}>
      {cachedAttempt && onStartOver && (
        <div
          className={`mx-4 mt-4 md:mx-6 p-3 rounded-lg border ${
            darkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'
          }`}
        >
          <div className="flex items-start gap-2">
            <AlertCircle
              className={`w-4 h-4 flex-shrink-0 mt-0.5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}
            />
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-medium ${darkMode ? 'text-amber-200' : 'text-amber-900'}`}
              >
                You already have a conversation in progress
              </p>
              <p className={`mt-1 text-xs ${darkMode ? 'text-amber-300/80' : 'text-amber-800'}`}>
                Start over to clear it and begin from the beginning.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onStartOver}
            disabled={isLoading || isStreaming}
            className={`mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              darkMode
                ? 'bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-100'
                : 'bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-950'
            }`}
          >
            <RotateCcw size={14} />
            Start over
          </button>
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {messages.map(message => (
          <RoaryMessage
            key={message.id}
            message={message}
            darkMode={darkMode}
            onQuickReply={onQuickReply}
          />
        ))}

        {/* Thinking indicator */}
        {isThinking && !currentToolName && (
          <div
            className={`flex items-center gap-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
          >
            <img src={roaryMascot} alt="Roary" className="w-8 h-8 object-contain animate-pulse" />
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Roary is thinking...</span>
            </div>
          </div>
        )}

        {/* Tool execution indicator */}
        {currentToolName && (
          <div
            className={`flex items-center gap-3 text-sm px-3 py-2 rounded-lg ${
              darkMode
                ? 'text-orange-400 bg-orange-500/10 border border-orange-500/20'
                : 'text-blue-600 bg-blue-50'
            }`}
          >
            <img src={roaryMascot} alt="Roary" className="w-8 h-8 object-contain" />
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 animate-pulse" />
              <span>Working on: {formatToolName(currentToolName)}</span>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div
            className={`flex items-start gap-2 text-sm px-3 py-2 rounded-lg ${
              darkMode ? 'text-red-400 bg-red-500/10' : 'text-red-600 bg-red-50'
            }`}
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button
              onClick={onClearError}
              className={`p-0.5 rounded ${darkMode ? 'hover:bg-red-500/20' : 'hover:bg-red-100'}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Menu-only greeting: hide the composer until they pick a path */}
      {phase !== 'greeting' && (
        <div
          className={`p-4 md:p-6 ${
            darkMode
              ? `border-t bg-slate-900/50 ${isDepleted ? 'border-red-900/50' : 'border-slate-800/50'}`
              : `border-t bg-white ${isDepleted ? 'border-red-300' : 'border-gray-200'}`
          }`}
        >
          <form onSubmit={handleSubmit} className="flex gap-2 max-w-3xl mx-auto">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={getPlaceholder(phase, isStreaming, isDepleted)}
                disabled={isStreaming || isLoading || isDepleted}
                autoFocus
                rows={1}
                className={`w-full px-4 py-3 pr-12 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  darkMode
                    ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500 disabled:bg-slate-800/50 disabled:text-gray-600'
                    : 'bg-white border border-gray-300 disabled:bg-gray-50 disabled:text-gray-500'
                }`}
              />
            </div>

            {isStreaming ? (
              <button
                type="button"
                onClick={onCancel}
                className={`px-4 py-3 rounded-xl transition-colors flex items-center gap-2 ${
                  darkMode
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    : 'bg-red-100 text-red-600 hover:bg-red-200'
                }`}
              >
                <StopCircle className="w-5 h-5" />
                <span className="hidden sm:inline">Stop</span>
              </button>
            ) : (
              <button
                type="submit"
                disabled={!input.trim() || isLoading || isDepleted}
                className={`px-4 py-3 rounded-xl transition-colors flex items-center gap-2 ${
                  darkMode
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-400 hover:to-orange-500 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500'
                    : 'bg-orange-500 text-white hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed'
                }`}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">Send</span>
              </button>
            )}
          </form>

          {/* Helper text */}
          <p className={`text-xs mt-2 text-center ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      )}
    </div>
  );
};
